<template>
  <div ref="root" class="theme-picker" :class="{ 'placement-up': placement === 'up' }">
    <button
      class="theme-trigger"
      type="button"
      :aria-label="t('app.theme.label')"
      aria-haspopup="menu"
      :aria-expanded="open"
      @click="open = !open"
    >
      <span class="theme-preview" :class="`preview-${current.value}`">
        <UIcon :name="current.icon" />
      </span>
      <span class="theme-current">{{ current.label }}</span>
      <UIcon class="theme-chevron" name="i-lucide-chevron-down" />
    </button>

    <Transition name="theme-menu">
      <div v-if="open" class="theme-options" role="menu" :aria-label="t('app.theme.label')">
        <button
          v-for="item in themeOptions"
          :key="item.value"
          type="button"
          role="menuitemradio"
          :aria-checked="item.value === appStore.theme"
          :class="{ active: item.value === appStore.theme }"
          @click="change(item.value)"
        >
          <span class="theme-preview" :class="`preview-${item.value}`">
            <UIcon :name="item.icon" />
          </span>
          <span>{{ item.label }}</span>
          <UIcon
            :name="item.value === appStore.theme ? 'i-lucide-check' : 'i-lucide-chevron-right'"
          />
        </button>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { onClickOutside } from '@vueuse/core'
import { useI18n } from 'vue-i18n'
import { useAppStore, type ThemeName } from '@/stores/app'

const { t } = useI18n()
const { placement = 'down' } = defineProps<{ placement?: 'down' | 'up' }>()
const appStore = useAppStore()
const root = ref<HTMLElement>()
const open = ref(false)
const themeOptions = computed(() => [
  { value: 'light' as ThemeName, label: t('app.theme.light'), icon: 'i-lucide-sun' },
  { value: 'dark' as ThemeName, label: t('app.theme.dark'), icon: 'i-lucide-moon' },
  { value: 'orange' as ThemeName, label: t('app.theme.orange'), icon: 'i-lucide-sunset' },
  { value: 'blue' as ThemeName, label: t('app.theme.blue'), icon: 'i-lucide-briefcase-business' },
])
const current = computed(
  () => themeOptions.value.find((item) => item.value === appStore.theme) || themeOptions.value[0],
)
const change = (theme: ThemeName) => {
  appStore.setTheme(theme)
  open.value = false
}
onClickOutside(root, () => {
  open.value = false
})
</script>

<style scoped>
.theme-picker {
  position: relative;
  flex: none;
}
.theme-trigger {
  min-width: 116px;
  height: 42px;
  padding: 0 10px 0 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid var(--fb-line);
  border-radius: 12px;
  outline: none;
  background: color-mix(in srgb, var(--fb-surface) 92%, transparent);
  color: var(--fb-ink);
  box-shadow: 0 4px 14px rgba(10, 37, 51, 0.04);
  cursor: pointer;
  transition:
    border-color 0.18s,
    box-shadow 0.18s,
    transform 0.18s;
}
.theme-trigger:hover,
.theme-trigger:focus-visible {
  border-color: color-mix(in srgb, var(--fb-coral) 48%, var(--fb-line));
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--fb-coral) 10%, transparent);
}
.theme-preview {
  flex: none;
  width: 28px;
  height: 28px;
  display: grid;
  place-items: center;
  border: 1px solid transparent;
  border-radius: 9px;
  font-size: 15px;
}
.preview-light {
  border-color: #bfe0d6;
  background: #e4f5f0;
  color: #08735f;
}
.preview-dark {
  border-color: #514477;
  background: #211b34;
  color: #c4b5fd;
}
.preview-orange {
  border-color: #f0ceb0;
  background: #fff0df;
  color: #c85c1a;
}
.preview-blue {
  border-color: #c8d9ee;
  background: #e8f0ff;
  color: #2864bd;
}
.theme-current {
  min-width: 0;
  flex: 1;
  overflow: hidden;
  font-size: 12px;
  font-weight: 750;
  text-align: start;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.theme-chevron {
  flex: none;
  color: var(--fb-ink-soft);
  transition: transform 0.18s;
}
.theme-trigger[aria-expanded='true'] .theme-chevron {
  transform: rotate(180deg);
}
.theme-options {
  position: absolute;
  inset-inline-end: 0;
  top: calc(100% + 8px);
  z-index: 100;
  width: 190px;
  padding: 7px;
  border: 1px solid var(--fb-line);
  border-radius: 14px;
  background: var(--fb-surface-raised);
  box-shadow: var(--fb-shadow);
}
.theme-options button {
  width: 100%;
  min-height: 44px;
  padding: 0 9px;
  display: grid;
  grid-template-columns: 30px 1fr auto;
  align-items: center;
  gap: 10px;
  border: 0;
  border-radius: 9px;
  background: transparent;
  color: var(--fb-ink);
  font-size: 12px;
  font-weight: 680;
  text-align: start;
  cursor: pointer;
}
.theme-options button:hover {
  background: var(--fb-paper);
}
.theme-options button.active {
  background: var(--fb-coral-soft);
  color: var(--fb-coral-dark);
  font-weight: 780;
}
.theme-options button.active .theme-preview {
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--fb-coral) 18%, transparent);
}
.theme-menu-enter-active,
.theme-menu-leave-active {
  transition:
    opacity 0.15s,
    transform 0.15s;
  transform-origin: top right;
}
.theme-menu-enter-from,
.theme-menu-leave-to {
  opacity: 0;
  transform: translateY(-5px) scale(0.98);
}
.theme-picker.placement-up .theme-options {
  top: auto;
  bottom: calc(100% + 8px);
}
@media (max-width: 520px) {
  .theme-trigger {
    min-width: 108px;
  }
}
</style>
